// Help text for: Tools > Efficiency Initiatives.
// Keys are referenced as "efficiencyInitiatives.<field>" by <FieldHelp id=... />. Entry shape:
//   title  - field name as shown on screen
//   use    - what the field is for / how to read it
//   source - measured | calculated | backend | default | setting | input
//   how    - where the value comes from: formula, default value, or who sets it
const help = {
  /* ---------------------------------------------------- summary tiles */
  totalInitiatives: {
    title: "Total Initiatives",
    use: "How many initiatives match the filters you have set, and how many of those are still only Planned.",
    source: "calculated",
    how: "Counts every matching initiative, not just the rows you can see on screen. Deleted ones are not counted.",
  },
  activeCount: {
    title: "Active",
    use: "Initiatives being carried out right now.",
    source: "calculated",
    how: "Counts matching initiatives marked Active.",
  },
  completedCount: {
    title: "Completed",
    use: "Initiatives that have been finished, with the number cancelled shown underneath.",
    source: "calculated",
    how: "Counts matching initiatives marked Completed, and underneath, those marked Cancelled.",
  },
  totalImplementationCost: {
    title: "Implementation Cost",
    use: "What all the matching initiatives cost to carry out, added together.",
    source: "calculated",
    how: "Adds up the Implementation cost (PKR) typed in on every matching initiative. Ones with no cost entered count as zero.",
  },
  totalExpectedSaving: {
    title: "Expected Annual Saving",
    use: "The yearly saving these initiatives were meant to deliver. This is a target, not a measured result.",
    source: "calculated",
    how: "Adds up the Expected annual saving in kWh and in PKR typed in on every matching initiative. What actually happened is on the Performance and Overall Impact views.",
  },

  /* ---------------------------------------------------- filters */
  filterStatus: {
    title: "Status filter",
    use: "Shows only initiatives at the state you pick. It also applies to the Performance and Overall Impact views.",
    source: "input",
    how: "Starts on All statuses.",
  },
  filterNodeType: {
    title: "Node type filter",
    use: "Shows only initiatives made on a Building, a Section or a Machine.",
    source: "input",
    how: "Starts on All nodes.",
  },
  filterInsight: {
    title: "Insight filter",
    use: "Shows only initiatives aimed at the problem you pick.",
    source: "input",
    how: "Starts on All insights. The choices are the same list of problems enSight can spot.",
  },
  filterFrom: {
    title: "Starting from",
    use: "Shows only initiatives that started on or after this day.",
    source: "input",
    how: "Empty to begin with, meaning no limit. Compared against each initiative's Start date.",
  },
  filterTo: {
    title: "Starting up to",
    use: "Shows only initiatives that started on or before this day.",
    source: "input",
    how: "Empty to begin with, meaning no limit. Compared against each initiative's Start date.",
  },
  filterSearch: {
    title: "Search",
    use: "Finds initiatives by words in their title, their ID or the owner's name.",
    source: "input",
    how: "Empty to begin with. Matches any part of the text, and capital letters do not matter.",
  },

  /* ---------------------------------------------------- record fields */
  referenceCode: {
    title: "ID",
    use: "The initiative's reference number, for quoting in reports and conversations.",
    source: "backend",
    how: "Handed out when the initiative is created: EI- followed by a six-digit running number for your company. Numbers from deleted initiatives are never used again.",
  },
  initiative: {
    title: "Initiative",
    use: "The initiative's title, with its description underneath.",
    source: "input",
    how: "What you typed into Title and Description when you created or edited it.",
  },
  node: {
    title: "Affected node",
    use: "The building, section or machine the change was made on. Its breaker readings are what the before-and-after saving is measured from.",
    source: "input",
    how: "You pick it from your site layout, and whether it counts as a Building, a Section or a Machine follows from where it sits there. For a building or section, the energy is the sum of the topmost metered points beneath it, leaving out solar and sensor meters.",
  },
  insights: {
    title: "Insights",
    use: "The problems (spotted by enSight) this initiative is meant to fix. They are used to check whether those problems really went away.",
    source: "input",
    how: "You pick them in the form and at least one is needed. This column shows the first two, with +N standing for the rest.",
  },
  owner: {
    title: "Owner",
    use: "The person responsible for getting the initiative done.",
    source: "input",
    how: "You type this in and it cannot be left blank. Up to 120 characters.",
  },
  ownerEmail: {
    title: "Owner email",
    use: "Contact email for the person who owns the initiative.",
    source: "input",
    how: "You type this in, or leave it out.",
  },
  status: {
    title: "Status",
    use: "Where the initiative stands: Planned, Active, Completed or Cancelled.",
    source: "input",
    how: "New initiatives start as Planned. You change it in the form, or with the Complete button. Cancelled ones are left out of every measured result.",
  },
  startDate: {
    title: "Start",
    use: "The day the initiative counts as having started. The date filters use this.",
    source: "backend",
    how: "Worked out for you: the Implemented on date if you set one; if not, the day after the before-period ends; if not, the day the initiative was created.",
  },
  implementationCost: {
    title: "Implementation cost (PKR)",
    use: "What the initiative cost to carry out. Used for payback, net position and return on investment.",
    source: "input",
    how: "You type this in and it cannot be negative. Left blank, it is saved as zero.",
  },
  title: {
    title: "Title",
    use: "A short name for the change, for example \"VFD installed on Compressor 2\".",
    source: "input",
    how: "You type this in and it cannot be left blank. Up to 200 characters.",
  },
  description: {
    title: "Description",
    use: "What was changed, and why.",
    source: "input",
    how: "You type this in, or leave it out.",
  },
  insightPicker: {
    title: "Insights this initiative addresses",
    use: "Tick every problem this change is meant to fix. One change often fixes several at once — a capacitor bank, for instance, fixes both poor power factor and reactive-energy charges.",
    source: "input",
    how: "At least one is needed. The choices and the groups they sit in are the standard list of problems enSight can spot. They drive the insight-loss comparison and the Estimate from selected insights button.",
  },
  implementedOn: {
    title: "Implemented on",
    use: "The day the change took effect. It is the dividing line between \"before\" and \"after\" in the readings.",
    source: "input",
    how: "You can leave it out while you are still planning. It must fall after the before-period ends. If it is still blank when you mark the initiative Completed, today's date is used. The after period runs from this day up to today.",
  },
  baselineStart: {
    title: "Baseline start",
    use: "First day of the \"before\" period that the after period is measured against.",
    source: "input",
    how: "You can leave this out. If you do, a 30-day before-period is used, ending on the Baseline end date, or on the day before the start date.",
  },
  baselineEnd: {
    title: "Baseline end",
    use: "Last day of the \"before\" period.",
    source: "input",
    how: "You can leave this out; the day before the start date is then used. The before-period must run at least 7 days and must end before the Implemented on date.",
  },
  expectedKwh: {
    title: "Expected annual saving (kWh)",
    use: "The energy you expect the initiative to save each year. What is actually measured gets scored against it, as attainment.",
    source: "input",
    how: "You type it in, or let Estimate from selected insights fill it: the energy those problems wasted on this node during the before-period, per day, times 365. Most problems do not have a measured energy waste, so this box is usually yours to fill.",
  },
  expectedPkr: {
    title: "Expected annual saving (PKR)",
    use: "The money you expect the initiative to save each year. What is actually measured gets scored against it, as attainment.",
    source: "input",
    how: "You type it in, or let Estimate from selected insights fill it: the production loss those problems caused on this node during the before-period, per day, times 365. The before-period has to fall inside the last 15 days, because the minute-by-minute readings the problem detectors need are only kept that long.",
  },
  notes: {
    title: "Notes & implementation details",
    use: "Any extra detail about how the change was carried out.",
    source: "input",
    how: "You type this in, or leave it out.",
  },
  startEnd: {
    title: "Start · End",
    use: "The day the initiative started and, if one was set, the day it ended.",
    source: "backend",
    how: "The start is worked out for you: Implemented on, or else the day after the before-period ends, or else the day it was created. The end date is no longer asked for in the form; where one exists, it cuts the after period short.",
  },
  implementedVerified: {
    title: "Implemented · Verified",
    use: "When the change took effect, and when someone checked it was actually working.",
    source: "input",
    how: "You enter both in the form or in the Complete dialog. Implemented on falls back to the day you marked it complete if left blank. Verified on can be left out, but cannot be earlier than Implemented on.",
  },
  baselinePeriod: {
    title: "Baseline period",
    use: "The \"before\" stretch of readings that the results are measured against.",
    source: "input",
    how: "The dates you entered. \"Derived automatically\" means you entered none, so the 30 days ending the day before the start date are used.",
  },
  expectedAnnualSaving: {
    title: "Expected annual saving",
    use: "The yearly kWh and PKR saving this initiative was meant to deliver.",
    source: "input",
    how: "Entered in the form, either by hand or with Estimate from selected insights. It is only a target; what actually happened is under Measured impact.",
  },
  activityHistory: {
    title: "Activity history",
    use: "Every change made to this initiative, who made it and when.",
    source: "backend",
    how: "Written down automatically each time the initiative is created, edited, moved to a new status, or has a document added or removed. Edits list the old and new value of everything that changed.",
  },

  /* ---------------------------------------------------- complete dialog & documents */
  completeImplementedOn: {
    title: "Implemented on",
    use: "The day the change took effect; it splits \"before\" from \"after\" for measuring the saving.",
    source: "input",
    how: "Comes pre-filled with the date already on the initiative — change it if that is wrong. Left blank, today's date is used.",
  },
  verifiedOn: {
    title: "Verified on",
    use: "The day someone checked the change was actually working.",
    source: "input",
    how: "You can leave this out. Comes pre-filled with the date already saved. It cannot be earlier than Implemented on.",
  },
  supportingDocuments: {
    title: "Supporting documents",
    use: "Evidence for the initiative, such as invoices, photographs or reports.",
    source: "input",
    how: "You can leave this out. PDF, images, Word, Excel, CSV or plain text, up to 10 MB each, 10 files per upload and 20 in all on one initiative.",
  },
  completeNote: {
    title: "Note",
    use: "What was done and who signed it off. It is kept in the activity history.",
    source: "input",
    how: "You can leave this out; a short standard note saying it was marked complete from the initiatives list is then recorded.",
  },

  /* ---------------------------------------------------- measured impact */
  measuredImpact: {
    title: "Measured impact",
    use: "What actually changed on the affected node's breaker readings after the initiative, set against the before-period.",
    source: "calculated",
    how: "Worked out afresh from the hourly readings every time you open it. Only days with at least 12 hours of readings count, and each period needs at least 7 such days. The two periods are compared as an average day, because they are different lengths.",
  },
  confidence: {
    title: "Confidence",
    use: "How much weight you can put on the measured saving: High, Medium or Low.",
    source: "calculated",
    how: "Four checks: how long the before-period is, how long the after period is (good at 28 counted days or more, fair at 14 or more), how many days in the weaker period actually have readings (good at 90% or more, fair at 75% or more), and how big the saving is next to the node's normal day-to-day swing. Scored out of 100; one poor check holds it at Medium, two hold it at Low.",
  },
  energySaved: {
    title: "Energy saved",
    use: "Energy saved since the change was made, with the yearly rate underneath.",
    source: "calculated",
    how: "Average kWh a day before, less average kWh a day after, times the number of counted days since it was implemented. The yearly figure is that same daily saving times 365. Solar nodes work the other way round: generating more counts as a saving. A minus figure means usage went up.",
  },
  financialSaving: {
    title: "Financial saving",
    use: "Money saved since the change was made, with the yearly rate underneath.",
    source: "calculated",
    how: "Cost of an average day before, less cost of an average day after, times the counted days since it was implemented; times 365 for the yearly figure. A day's cost is peak-hour units at your peak price plus all other units at your normal price, so shifting load out of peak hours shows up as a saving. With no unit price set, 50 PKR per unit is assumed.",
  },
  payback: {
    title: "Payback",
    use: "How long the initiative takes to earn back what it cost, at the saving rate measured so far.",
    source: "calculated",
    how: "Implementation cost ÷ the yearly PKR saving, shown in months below 24 and in years above. It reads \"No payback\" when the measured saving is zero or worse.",
  },
  co2Avoided: {
    title: "CO₂ avoided",
    use: "Carbon emissions kept out of the air by the energy you saved.",
    source: "default",
    how: "Shows N/A because nobody has agreed a figure for how much carbon each unit of grid electricity carries (kg CO₂ per kWh). Once one is set, this becomes the energy saved times that figure.",
  },
  peakDemand: {
    title: "Peak demand",
    use: "The heaviest load on the node before and after, and how far it came down.",
    source: "calculated",
    how: "Highest hourly average load (kW) across the counted days of each period. The reduction is before less after. It is not reported as a reduction for solar nodes.",
  },
  netPosition: {
    title: "Net position",
    use: "Whether the initiative has paid for itself yet.",
    source: "calculated",
    how: "Money saved so far, less what it cost to carry out. Zero or better means the cost is back in your pocket.",
  },
  impactTrend: {
    title: "Before and after consumption trend",
    use: "Energy per day, week, month or year right across the before and after periods, with a marker on the day the change was made.",
    source: "measured",
    how: "Adds up the hourly breaker energy in each bar. The Daily, Weekly, Monthly and Yearly buttons only change the width of the bars, never the figures above. A button is greyed out when a period is too short to draw that way.",
  },
  beforeAfter: {
    title: "Before vs after",
    use: "The node's average day in the before-period, set beside its average day since the change.",
    source: "calculated",
    how: "Averaged over the counted days — those with at least 12 hours of readings — in each period.",
  },
  avgDaily: {
    title: "Avg daily consumption",
    use: "Energy the node uses on an average day, or for solar, generates.",
    source: "calculated",
    how: "Total kWh across the counted days ÷ how many counted days there were, for each period. The hourly energy is read off the meter's own energy count at the breaker; impossible jumps — more than 4 times what the measured load could have delivered in that hour — are thrown out.",
  },
  avgDailyCost: {
    title: "Avg daily cost",
    use: "What an average day's energy cost, in each period.",
    source: "calculated",
    how: "Average peak-hour units at your peak price, plus average other units at your normal price. Peak hours shift with the month: 5–9 PM in December to February, 6–10 PM in March to May and September to November, 7–11 PM in June to August.",
  },
  percentChange: {
    title: "Change",
    use: "How far average daily usage moved after the change, as a percentage.",
    source: "calculated",
    how: "Average kWh a day before, less after, divided by the before figure, times 100. A minus sign means usage fell, which is the good direction. It reads n/a when the before-period was under 1 kWh a day.",
  },
  expectedVsActual: {
    title: "Expected vs actual (annualised)",
    use: "Puts the yearly saving you expected next to the yearly rate actually being measured.",
    source: "calculated",
    how: "Expected is what was typed on the initiative. Actual is the measured daily saving times 365.",
  },
  attainment: {
    title: "Attainment",
    use: "How much of its expected saving the initiative is actually delivering.",
    source: "calculated",
    how: "Measured yearly saving ÷ expected yearly saving, times 100. Green at 100% or above, amber from 75% up, red below that. It reads \"Worse than baseline\" when the measured saving is negative, and \"no target\" when no expected saving was ever entered.",
  },

  /* ---------------------------------------------------- insight loss */
  insightLoss: {
    title: "Insight loss",
    use: "Whether the problems this initiative went after really did go away, and how much yearly production loss that removed.",
    source: "calculated",
    how: "The enSight problem detectors are run over the before and after periods, for this node and everything under it. The headline is the drop in production loss per day times 365, with any problem that got worse netted off. It needs minute-by-minute readings, which are only kept for 15 days.",
  },
  lossOutcome: {
    title: "Outcome",
    use: "What happened to each problem you were targeting after the change was made.",
    source: "calculated",
    how: "Resolved: found before, gone after. Improved or Worsened: found in both, with the loss per day down or up by more than 1%. New: only found afterwards. Not detected: found in neither. Not measured: the detector could not be run.",
  },
  lossChange: {
    title: "Change",
    use: "How far this problem's production loss per day came down.",
    source: "calculated",
    how: "Loss per day before, less loss per day after, divided by the before figure, times 100. Left blank when the before-period loss was under 1 PKR a day.",
  },
  lossPerYear: {
    title: "Per year",
    use: "Yearly production loss removed by fixing this problem. A minus figure means it got worse.",
    source: "calculated",
    how: "Loss per day before, less loss per day after, times 365. The loss itself is the hours the problem lasted times the machine's production value per hour, and 5,000 PKR an hour is assumed where none is set for that machine.",
  },
  lossPerDay: {
    title: "Per day vs window totals",
    use: "Each problem's production loss per day in both periods, with the raw totals underneath.",
    source: "calculated",
    how: "Per day is the loss over the period ÷ the days that actually have readings. The raw totals cannot be compared with each other, because the two periods are different lengths.",
  },
  fixedRisk: {
    title: "Risk no longer carried",
    use: "Equipment and maintenance risk that went away along with the problems you resolved.",
    source: "calculated",
    how: "Adds up the equipment and maintenance share of the loss for the problems now resolved. That share is charged once each time a problem is spotted rather than by the hour, so it is kept out of the saving figures.",
  },

  /* ---------------------------------------------------- performance view */
  performanceTitle: {
    title: "Initiative performance",
    use: "Ranks your initiatives by what they actually delivered, and picks out the best and those falling short of their targets.",
    source: "calculated",
    how: "The measured impact is worked out for each initiative, up to the 100 most recent, exactly as it is on the initiative's own page. Cancelled ones, and any without enough readings, are left out of the ranking.",
  },
  rank: {
    title: "#",
    use: "Where the initiative sits in the current ranking. A star marks the top three.",
    source: "calculated",
    how: "Sorted on whichever column is highlighted: biggest first for energy and money saved, shortest first for payback.",
  },
  perfEnergy: {
    title: "Energy (kWh/yr)",
    use: "The yearly energy saving actually measured for each initiative.",
    source: "calculated",
    how: "Average kWh a day before, less average kWh a day after, times 365, from the node's breaker readings.",
  },
  perfFinancial: {
    title: "Financial (PKR/yr)",
    use: "The yearly money saving actually measured for each initiative.",
    source: "calculated",
    how: "Cost of an average day before, less cost of an average day after, times 365, priced at your peak and off-peak unit rates.",
  },
  perfPayback: {
    title: "Payback",
    use: "How long each initiative takes to earn back what it cost.",
    source: "calculated",
    how: "Implementation cost ÷ the measured yearly PKR saving. It reads \"No payback\" when nothing is being saved.",
  },
  perfLoss: {
    title: "Loss reduction (PKR/yr)",
    use: "Yearly production loss removed from the problems each initiative went after.",
    source: "calculated",
    how: "The net drop in production loss per day times 365, from the problem detectors. This is slow work: at most 10 initiatives are measured, within 45 seconds, so the rest may read \"not measured\".",
  },
  overallTop: {
    title: "Highest performing overall",
    use: "The initiatives that do best across everything at once, judged against each other rather than against their own targets.",
    source: "calculated",
    how: "Each initiative's average placing across energy saved, money saved and payback, with loss reduction added in when every initiative has it. The lowest average wins, and the top three are shown.",
  },
  underperformers: {
    title: "Below expected performance",
    use: "Initiatives saving less energy than they were expected to.",
    source: "calculated",
    how: "Listed when the measured yearly kWh saving ÷ the expected yearly kWh saving comes out under 100%. Initiatives with no expected saving cannot be judged, so they are listed on their own.",
  },
  notMeasurable: {
    title: "Not yet measurable",
    use: "Initiatives without enough readings yet to put a saving on them.",
    source: "calculated",
    how: "Each period needs at least 7 days with 12 hours of readings or more. What they cost is still counted in the Overall Impact totals.",
  },

  /* ---------------------------------------------------- overall impact view */
  overallTitle: {
    title: "Overall impact",
    use: "The totals across every measured initiative: energy, money, cost, return and payback.",
    source: "calculated",
    how: "Adds up the measured impact of every initiative in scope — Cancelled ones left out, up to the 100 most recent. Where two initiatives sit one inside the other, the same energy can be counted twice, and a warning appears when that happens.",
  },
  totalEnergySaved: {
    title: "Total energy saved",
    use: "Energy saved so far by every measured initiative, with the combined yearly rate underneath.",
    source: "calculated",
    how: "Adds up each initiative's Energy saved, which is its daily saving times its counted days since implementation. The yearly rate adds up each daily saving and multiplies by 365.",
  },
  totalFinancialSavings: {
    title: "Total financial savings",
    use: "Money saved so far by every measured initiative, with the combined yearly rate underneath.",
    source: "calculated",
    how: "Adds up each initiative's Financial saving so far. The yearly rate adds up each initiative's yearly PKR saving.",
  },
  totalImplementationCostOverall: {
    title: "Total implementation cost",
    use: "What every initiative in scope cost, whether or not its saving can be measured yet.",
    source: "calculated",
    how: "Adds up the Implementation cost on every initiative that has not been cancelled, including the ones not yet measurable.",
  },
  overallRoi: {
    title: "Overall ROI",
    use: "The return so far on the money you have spent, with the yearly return underneath.",
    source: "calculated",
    how: "Money saved so far, less total cost, divided by total cost, times 100. The yearly figure is the yearly saving rate ÷ total cost, times 100.",
  },
  portfolioPayback: {
    title: "Payback period",
    use: "How long everything together takes to earn back what it all cost.",
    source: "calculated",
    how: "Total cost ÷ one twelfth of the yearly saving rate, giving months; shown in years once it reaches 24 months. It reads \"No payback\" when the combined saving is zero or worse.",
  },
  portfolioNetPosition: {
    title: "Net position",
    use: "Whether the whole set of initiatives has paid for itself yet.",
    source: "calculated",
    how: "Money saved so far across them all, less everything they cost to carry out.",
  },
  totalPeakReduction: {
    title: "Total peak reduction",
    use: "The combined drop in peak demand across your initiatives.",
    source: "calculated",
    how: "Adds up each initiative's own peak demand reduction in kW. Your site's real peak will fall by less than this, because the different nodes do not all peak at the same moment.",
  },
  totalCo2: {
    title: "Total CO₂ avoided",
    use: "Carbon emissions kept out of the air by all your initiatives together.",
    source: "default",
    how: "Shows N/A because nobody has agreed a figure for how much carbon each unit of grid electricity carries (kg CO₂ per kWh).",
  },
  totalLossReduction: {
    title: "Total loss reduction",
    use: "Production loss removed so far across your initiatives, from the problems they went after, with the yearly rate underneath.",
    source: "calculated",
    how: "Nothing is worked out until you press Calculate, because it runs the problem detectors for every initiative. At most 10 are measured, within 45 seconds, so it may only cover part of the set — shown as \"N of M measured\".",
  },
  cumulativeChart: {
    title: "Cumulative impact over time",
    use: "Month by month, total savings against total spend. Where Net position crosses zero is the month it all paid for itself.",
    source: "calculated",
    how: "Each initiative adds its measured daily saving for every day since it was implemented, and its whole cost in the month it was implemented. \"Measured only\" counts just the days that have readings, so it matches the tiles above.",
  },
  shareOfSavings: {
    title: "Share of savings",
    use: "How much of the yearly money saving across everything comes from this one initiative.",
    source: "calculated",
    how: "This initiative's Financial (PKR/yr) ÷ the combined yearly saving rate, times 100.",
  },
};

export default help;
